import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown } from 'react-icons/fa';

// Components
import TextInput from '../TextInput';
import CustomButton from '../CustomButton';
import Typography from '../Typography';

// Styles
import styles from './style.module.scss';

// Types
interface Option {
  label: string;
  value: string;
}

interface SelectOptionProps {
  label: string;
  value: string;
  selected: boolean;
  onSelect: (value: string) => void;
}

const SelectOption: React.FC<SelectOptionProps> = ({
  label, value, selected, onSelect
}) => {
  const handleSelect = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onSelect(value);

  }, [onSelect, value]);

  return (
    <div
      className={styles.option}
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={handleSelect}
    >
      <Typography variant='p' >
        {label}
      </Typography>
    </div>
  )
}

interface SelectProps {
  options: Option[];
  value: string;
  onChange?: (value: string) => void;
  label?: string;
  helper?: string;
  error?: string | null;
  disabled?: boolean;
  onRenderOption?: (option: Option) => React.ReactNode;
}

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  label,
  helper,
  error,
  disabled,
  onRenderOption
}) => {
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<any | null>(value || null);
  const ref = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [renderAbove, setRenderAbove] = useState(false);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        ref.current && !ref.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      // Temporarily set below by default
      setDropdownStyle({
        position: 'absolute',
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 1000,
      });
      setRenderAbove(false);
      // Wait for dropdown to render, then check space
      setTimeout(() => {
        if (dropdownRef.current) {
          const dropdownHeight = dropdownRef.current.offsetHeight;
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;
          if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
            // Render above
            setDropdownStyle({
              position: 'absolute',
              top: rect.top + window.scrollY - dropdownHeight,
              left: rect.left + window.scrollX,
              width: rect.width,
              zIndex: 1000,
            });
            setRenderAbove(true);
          } else {
            // Render below (default)
            setDropdownStyle({
              position: 'absolute',
              top: rect.bottom + window.scrollY,
              left: rect.left + window.scrollX,
              width: rect.width,
              zIndex: 1000,
            });
            setRenderAbove(false);
          }
        }
      }, 0);
    }
  }, [open]);

  const handleSelect = useCallback((value: string) => {
    setOpen(false);
    setSelectedOption(value);
    onChange?.(value)
  }, [onChange]);

  const DisplayedValue = useMemo(() => {
    return options.find((option) => option.value === selectedOption)?.label || '';
  }, [options, selectedOption]);

  return (
    <div
      className={styles.root}
      ref={ref}
      onClick={() => !disabled && setOpen((prev) => !prev)}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <TextInput
        label={label}
        helper={helper}
        error={error}
        value={DisplayedValue}
        readOnly
        disabled={disabled}
        type="text"
        // Remove onClick from TextInput, rely on parent div
        adornmentRight={
          <CustomButton
          // Remove onClick from CustomButton, rely on parent div
          // Removed tabIndex, not supported by CustomButton
          >
            <FaChevronDown />
          </CustomButton>
        }
        classes={{ input: styles.selectInput }}
      />
      {open && typeof window !== 'undefined' && createPortal(
        <div
          className={styles.options}
          role="listbox"
          ref={dropdownRef}
          style={dropdownStyle}
        >
          {onRenderOption ? (
            options.map((option) => onRenderOption(option))
          ) : (
            options.map((option) => (
              <SelectOption
                key={option.value}
                label={option.label}
                value={option.value}
                selected={selectedOption === option.value}
                onSelect={handleSelect}
              />
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default Select;
